import { FC, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { CounterpartyLookupListItem } from "../../api/Api";
import {
  useCounterpartyLookupDetails,
  useCounterpartyLookupNames
} from "./dataHooks/counterpartyLookupQueries";
import { LookupDetailsPanel } from "./lookupDetailsPanel";

type Props = {};

/**
 * Replaces the Excel sheet Compliance maintained by hand (ADHO-460): pick a counterparty by name,
 * see its KYC and credit risk status. Unlike the rest of the Hub this covers counterparties that
 * were never onboarded through the app, since it reads the data warehouse rather than blob storage.
 *
 * The selected id lives in the query string so a lookup can be linked to and survives a reload.
 */
export const CounterpartyLookup: FC<Props> = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("id") ?? undefined;
  const [inputValue, setInputValue] = useState("");

  const {
    data: names,
    isLoading: namesLoading,
    isError: namesError,
    error: namesErrorObject
  } = useCounterpartyLookupNames();

  const {
    data: details,
    isLoading: detailsLoading,
    isError: detailsError,
    error: detailsErrorObject
  } = useCounterpartyLookupDetails(selectedId);

  const options = useMemo(() => names ?? [], [names]);

  const selectedOption = useMemo(
    () => options.find((o) => o.id === selectedId) ?? null,
    [options, selectedId]
  );

  const onSelect = (value: CounterpartyLookupListItem | null) => {
    if (value?.id) {
      setSearchParams({ id: value.id });
    } else {
      setSearchParams({});
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
        minHeight: 0,
        padding: 2
      }}
    >
      {namesError && (
        <Alert severity="error">
          Could not load the counterparty list.{" "}
          {namesErrorObject instanceof Error ? namesErrorObject.message : ""}
        </Alert>
      )}

      <Box padding={1}>
        <Autocomplete
          options={options}
          loading={namesLoading}
          value={selectedOption}
          onChange={(_e, value) => onSelect(value)}
          inputValue={inputValue}
          onInputChange={(_e, value, reason) => {
            // Keep the selected name in the box when the picker closes rather than clearing it.
            if (reason !== "reset") setInputValue(value);
          }}
          getOptionLabel={(option) => option.name ?? option.id ?? ""}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          // The list runs to thousands of rows, so cap what is rendered per keystroke.
          filterOptions={(opts, state) => {
            const query = state.inputValue.trim().toLowerCase();
            const matches = query
              ? opts.filter(
                  (o) =>
                    o.name?.toLowerCase().includes(query) ||
                    o.id?.toLowerCase().includes(query)
                )
              : opts;
            return matches.slice(0, 100);
          }}
          sx={{ maxWidth: 640 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select counterparty"
              placeholder="Start typing a name or id"
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {namesLoading ? (
                        <CircularProgress color="inherit" size={18} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }
              }}
            />
          )}
        />
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {!selectedId && !detailsLoading && (
          <Typography variant="body2" color="text.secondary">
            Select a counterparty to see its details.
          </Typography>
        )}

        {detailsLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", padding: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {detailsError && (
          <Alert severity="error">
            Could not load details for this counterparty.{" "}
            {detailsErrorObject instanceof Error
              ? detailsErrorObject.message
              : ""}
          </Alert>
        )}

        {details && !detailsLoading && (
          <Box padding={1}>
            <LookupDetailsPanel details={details} />
          </Box>
        )}
      </Box>
    </Box>
  );
};
